/**
 * Deterministic parsing of a trainer's plain-English answers ("6–11 and 4–8, Saturdays 8 till 12").
 *
 * One code path for: the setup chat (#1), plain-text availability changes (#6), and as the fallback
 * and validator for model output. Rules:
 *  - extract only — never produce a window the text doesn't state (a time range, or a vague word that
 *    maps to a stated UK default);
 *  - if something is genuinely ambiguous, return ONE clarifying question, not a guess.
 */
import type { Weekday } from "./index.ts";
import { DAYS, UK_DEFAULTS, describeDays, describeWeek, normaliseSlots, toHHMM, toMin, type Window } from "./week.ts";

export type Parsed<T> = { ok: true; value: T; note?: string } | { ok: false; clarify: string; chips?: string[] };

// ------------------------------------------------------------------ session length
export function parseSessionLength(text: string): Parsed<number> {
  const t = text.toLowerCase().replace(/,/g, " ").trim();
  if (/\b(hour and a half|an hour and a half|one and a half hours?|1\s*½)/.test(t)) return ok(90);
  if (/\bhalf (an )?hour\b/.test(t)) return ok(30);
  const h = t.match(/(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours)\b/);
  if (h) return range(Math.round(Number(h[1]) * 60));
  if (/\b(an|one|1)\s*hour\b|^hour$/.test(t)) return ok(60);
  const m = t.match(/(\d{2,3})\s*(?:m|min|mins|minute|minutes)?\b/);
  if (m) return range(Number(m[1]));
  return { ok: false, clarify: "How many minutes is a usual session — 30, 45, 60 or 90?", chips: ["30 min", "45 min", "60 min", "90 min"] };

  function range(n: number): Parsed<number> {
    if (n >= 15 && n <= 240) return ok(Math.round(n / 5) * 5);
    return { ok: false, clarify: "That's outside what I can schedule (15 minutes to 4 hours). How long is a usual session?", chips: ["30 min", "45 min", "60 min", "90 min"] };
  }
}

// ------------------------------------------------------------------ days
const DAY_WORDS: [RegExp, Weekday][] = [
  [/^mon(day)?s?$/, "MON"],
  [/^tue(s|sday)?s?$/, "TUE"],
  [/^wed(s|nesday)?s?$/, "WED"],
  [/^thu(r|rs|rsday|sday)?s?$/, "THU"],
  [/^fri(day)?s?$/, "FRI"],
  [/^sat(urday)?s?$/, "SAT"],
  [/^sun(day)?s?$/, "SUN"],
];
const dayOf = (w: string): Weekday | null => DAY_WORDS.find(([re]) => re.test(w))?.[1] ?? null;
const span = (a: Weekday, b: Weekday): Weekday[] => {
  const i = DAYS.indexOf(a);
  const j = DAYS.indexOf(b);
  return i <= j ? DAYS.slice(i, j + 1) : [...DAYS.slice(i), ...DAYS.slice(0, j + 1)];
};

/** Day mentions in a fragment, including ranges ("mon-fri", "monday to thursday") and groups ("weekdays"). */
export function extractDays(text: string): Weekday[] {
  const t = ` ${text.toLowerCase().replace(/[–—]/g, "-")} `;
  const out = new Set<Weekday>();
  if (/\b(every ?day|daily|7 days|seven days|all week)\b/.test(t)) DAYS.forEach((d) => out.add(d));
  if (/\b(week ?days|weekday)\b/.test(t)) span("MON", "FRI").forEach((d) => out.add(d));
  if (/\bweek ?ends?\b/.test(t)) ["SAT", "SUN"].forEach((d) => out.add(d as Weekday));
  if (/\b(6|six) days\b/.test(t)) span("MON", "SAT").forEach((d) => out.add(d));
  const words = t.match(/[a-z]+|-/g) ?? [];
  for (let i = 0; i < words.length; i++) {
    const d = dayOf(words[i]!);
    if (!d) continue;
    const link = words[i + 1];
    const next = words[i + 2] ? dayOf(words[i + 2]!) : null;
    if (next && (link === "-" || link === "to" || link === "till" || link === "until" || link === "thru" || link === "through")) {
      span(d, next).forEach((x) => out.add(x));
      i += 2;
    } else out.add(d);
  }
  return DAYS.filter((d) => out.has(d));
}

export function parseDays(text: string): Parsed<Weekday[]> {
  const t = text.toLowerCase();
  // "Mon-Sat except Wednesday", "every day but Sunday", "not weekends"
  const [incl, excl] = t.split(/\b(?:except|apart from|but not|but|not|excluding|no)\b/, 2) as [string, string | undefined];
  let days = extractDays(incl);
  if (!days.length && excl !== undefined && /\b(every|all|7|seven)\b/.test(t) === false) days = [...DAYS]; // "not Sundays" ⇒ every other day
  if (!days.length && /^\s*(not|no|except)\b/.test(t)) days = [...DAYS];
  if (excl) {
    const drop = new Set(extractDays(excl));
    days = days.filter((d) => !drop.has(d));
  }
  if (!days.length) return { ok: false, clarify: "Which days do you see clients? For example “Mon–Fri” or “Mon, Wed and Sat”.", chips: ["Mon–Fri", "Mon–Sat", "Every day"] };
  return ok(days);
}

// ------------------------------------------------------------------ hours
type Mer = "am" | "pm" | null;
interface RawRange { a: number; am: number; aMer: Mer; b: number; bm: number; bMer: Mer }

const RANGE_RE = /(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?\s*(?:-|to|till|til|until|’?til)\s*(\d{1,2}|noon|midday)(?:[:.](\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?/;
const mer = (s?: string): Mer => (s ? (s.startsWith("a") ? "am" : "pm") : null);

/** Resolve a bare range like "4-8" or "9-1" to 24h minutes, using context (vague word) when given. */
function resolveRange(r: RawRange, hint: "am" | "pm" | null): { start: number; end: number } | "ambiguous" | null {
  let a = r.a;
  let b = r.b;
  const aMer = r.aMer ?? (r.bMer === "am" ? "am" : null);
  const bMer = r.bMer;
  const to24 = (h: number, m: Mer) => (m === "pm" && h < 12 ? h + 12 : m === "am" && h === 12 ? 0 : h);
  if (aMer && bMer) {
    a = to24(a, aMer);
    b = to24(b, bMer);
  } else if (bMer) {
    // "4-8pm" → 16–20; "10-2pm" / "10 till noon" → 10–14 / 10–12
    b = to24(b, bMer);
    if (bMer === "pm" && a < 12 && a + 12 < b) a += 12;
  } else if (aMer) {
    // "6am-12" → 06–12; "9am-1" → 09–13; "4pm-8" → 16–20
    a = to24(a, aMer);
    if (b < 12 && b + 12 > a && (aMer === "pm" || b <= a)) b += 12;
  } else if (hint === "pm") {
    if (a < 12) a += 12;
    if (b <= 12 && (b + 12) * 60 + r.bm > a * 60 + r.am) b += 12; // "3-3.30" → 15:00–15:30
  } else if (hint === "am") {
    if (b <= a) b += 12; // "9-1" in the morning context runs to 13:00
  } else {
    // No am/pm and no vague word: trainer hours are roughly 05:00–22:00.
    if (a <= 4) {
      a += 12;
      if (b <= 12) b += 12;
    } else if (a >= 5 && a <= 7 && b <= 9 && b > a) {
      return "ambiguous"; // "6-8": morning or evening?
    } else if (a === 12) {
      if (b < 12) b += 12;
    } else if (b <= a) {
      b += 12; // "9-1", "10-2"
    }
  }
  const start = a * 60 + r.am;
  const end = (b === 24 ? 24 * 60 : b * 60) + r.bm;
  if (!(end > start) || start < 0 || end > 24 * 60) return null;
  return { start, end };
}

function findRanges(fragment: string): RawRange[] {
  const out: RawRange[] = [];
  let s = fragment.replace(/[–—]/g, "-");
  for (let guard = 0; guard < 10; guard++) {
    const m = s.match(RANGE_RE);
    if (!m) break;
    const bRaw = m[4]!;
    const b = bRaw === "noon" || bRaw === "midday" ? 12 : Number(bRaw);
    out.push({ a: Number(m[1]), am: Number(m[2] ?? 0), aMer: mer(m[3]), b, bm: Number(m[5] ?? 0), bMer: bRaw === "noon" || bRaw === "midday" ? "pm" : mer(m[6]) });
    s = s.slice((m.index ?? 0) + m[0].length);
  }
  return out;
}

const VAGUE: { re: RegExp; key: keyof typeof UK_DEFAULTS; word: string; hint: "am" | "pm" | null }[] = [
  { re: /\bmornings?\b|\bam'?s\b/, key: "morning", word: "mornings", hint: "am" },
  { re: /\bafternoons?\b/, key: "afternoon", word: "afternoons", hint: "pm" },
  { re: /\bevenings?\b|\bnights?\b|after work/, key: "evening", word: "evenings", hint: "pm" },
  { re: /\ball day\b|\bfull days?\b|\bday ?time\b/, key: "allDay", word: "all day", hint: null },
];

export interface HoursResult {
  slots: Window[];
  /** Vague words resolved to UK defaults, to be stated back: ["mornings → 06:00–12:00"] */
  defaultsUsed: string[];
}

/**
 * Hours for the given working days. Clauses that name their own days apply only to those days
 * ("Saturdays 8 till 12"), and those days' general hours are replaced.
 */
export function parseHours(text: string, workingDays: Weekday[]): Parsed<HoursResult> {
  const t = text.toLowerCase().replace(/[–—]/g, "-");
  if (/\b(same|as usual)\b/.test(t) && !/\d/.test(t)) return { ok: false, clarify: "What times exactly? For example “6–11 and 4–8” or “mornings”.", chips: ["Mornings", "Evenings", "Mornings & evenings", "All day"] };
  // Split into clauses; a clause with days but no times carries its days forward ("Mon and Wed 6-9").
  const clauses = t.split(/[,;\n]|\band\b|\bthen\b|\bplus\b|\balso\b|&/).map((c) => c.trim()).filter(Boolean);
  const general: { start: number; end: number }[] = [];
  const specific = new Map<Weekday, { start: number; end: number }[]>();
  const defaultsUsed: string[] = [];
  let carryDays: Weekday[] = [];

  for (const c of clauses) {
    const days = extractDays(c);
    const ranges = findRanges(c);
    const vague = VAGUE.filter((v) => v.re.test(c));
    const hint = vague.find((v) => v.hint)?.hint ?? null;
    const target = [...carryDays, ...days];
    const windows: { start: number; end: number }[] = [];
    if (ranges.length) {
      for (const r of ranges) {
        const res = resolveRange(r, hint ?? null);
        if (res === "ambiguous") {
          const label = `${r.a}–${r.b}`;
          return { ok: false, clarify: `Is ${label} in the morning or the evening?`, chips: [`${label} morning`, `${label} evening`] };
        }
        if (!res) return { ok: false, clarify: `I couldn't read “${c}” as a time range. Could you say it like “6–11”?` };
        windows.push(res);
      }
    } else if (vague.length) {
      for (const v of vague) {
        const d = UK_DEFAULTS[v.key];
        windows.push({ start: toMin(d.startTime), end: toMin(d.endTime) });
        const used = `“${v.word}” as ${d.startTime}–${d.endTime}`;
        if (!defaultsUsed.includes(used)) defaultsUsed.push(used);
      }
    } else if (days.length) {
      carryDays = [...carryDays, ...days];
      continue;
    } else continue;

    if (target.length) {
      for (const d of target) specific.set(d, [...(specific.get(d) ?? []), ...windows]);
      carryDays = [];
    } else general.push(...windows);
  }

  const slots: Window[] = [];
  for (const d of DAYS) {
    const ws = specific.get(d) ?? (workingDays.includes(d) ? general : []);
    for (const w of ws) slots.push({ weekday: d, startTime: toHHMM(w.start), endTime: toHHMM(w.end) });
  }
  if (!slots.length) return { ok: false, clarify: "What hours do you usually work? For example “6–11 and 4–8, Saturdays 8–12”, or just “mornings”.", chips: ["Mornings", "Evenings", "Mornings & evenings", "All day"] };
  return ok({ slots: normaliseSlots(slots), defaultsUsed });
}

// ------------------------------------------------------------------ planning day
export type PlanningKey = "thu" | "fri" | "sat";
export function parsePlanningDay(text: string): Parsed<PlanningKey> {
  const d = extractDays(text);
  if (d.length === 1 && (d[0] === "THU" || d[0] === "FRI" || d[0] === "SAT")) return ok(d[0].toLowerCase() as PlanningKey);
  if (d.length === 1) return { ok: false, clarify: `I can plan on Thursday evening, Friday evening or Saturday morning (clients need time to reply before the week starts). Which suits you?`, chips: ["Thursday evening", "Friday evening", "Saturday morning"] };
  return { ok: false, clarify: "Which day should I ask your clients about the week ahead?", chips: ["Thursday evening", "Friday evening", "Saturday morning"] };
}

// ------------------------------------------------------------------ guard for model output
/**
 * True if every window in `slots` is supported by the text: its start and end hours appear in the text
 * (as a number), or it equals a UK default for a vague word the text uses. This is how we make sure the
 * model "never invents slots the trainer didn't say".
 */
export function slotsSupportedByText(text: string, slots: { startTime: string; endTime: string }[]): boolean {
  const t = text.toLowerCase();
  const nums = new Set((t.match(/\d{1,2}/g) ?? []).map(Number));
  if (/\bnoon|midday\b/.test(t)) nums.add(12);
  const defaults = VAGUE.filter((v) => v.re.test(t)).map((v) => UK_DEFAULTS[v.key]);
  const hourOk = (hhmm: string) => {
    const h = Number(hhmm.slice(0, 2));
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return nums.has(h) || nums.has(h12) || (h === 0 && nums.has(12));
  };
  return slots.every((s) => (hourOk(s.startTime) && hourOk(s.endTime)) || defaults.some((d) => d.startTime === s.startTime && d.endTime === s.endTime));
}

function ok<T>(value: T, note?: string): Parsed<T> {
  return { ok: true, value, note };
}

// ------------------------------------------------------------------ plain-text changes after setup (#6)
export interface AvailabilityChange {
  slots: Window[];
  /** Plain-words description of each change, for the confirmation */
  changes: string[];
}

type Op = "add" | "remove" | "set";
const OP_WORDS: [RegExp, Op][] = [
  [/\b(drop|remove|no more|no longer|stop|cancel|take off|get rid of|scrap|not doing)\b|\boff\b/, "remove"],
  [/\b(add|also|plus|extra|start doing|and now)\b/, "add"],
  [/\b(now|instead|change|move|switch|only|just|is|are)\b/, "set"],
];

/**
 * Apply "add Sunday mornings", "drop Friday evenings", "Mondays 7–10 now" to the current availability.
 * Same grammar as setup (parseHours). Returns one clarifying question when the intent or time is unclear.
 */
export function parseAvailabilityChange(text: string, current: Window[]): Parsed<AvailabilityChange> {
  const workingDays = DAYS.filter((d) => current.some((w) => w.weekday === d));
  let slots = normaliseSlots(current);
  const changes: string[] = [];
  const clauses = text.split(/[;.\n]|,(?![^,]*\d)|\bthen\b/i).map((c) => c.trim()).filter(Boolean);
  if (!clauses.length) return { ok: false, clarify: "What would you like to change? For example “add Sunday mornings” or “drop Friday evenings”." };

  for (const c of clauses) {
    const lower = c.toLowerCase();
    // ---- hours ----
    const op = OP_WORDS.find(([re]) => re.test(lower))?.[1] ?? null;
    const days = extractDays(lower);
    const stripped = lower.replace(/\b(drop|remove|no more|no longer|stop|cancel|take off|get rid of|scrap|not doing|off|add|also|plus|extra|start doing|now|instead|change|move|switch|only|just|is|are|to|my|the|on|do|doing|i'?m|i)\b/g, " ");
    const hasTimes = /\d|morning|afternoon|evening|night|all day/.test(stripped);
    if (!op) return { ok: false, clarify: `Should I add “${c}”, replace those hours, or remove it?`, chips: [`Add ${c}`, `Remove ${c}`] };
    if (!hasTimes && op === "remove" && days.length) {
      const before = slots.filter((w) => days.includes(w.weekday));
      if (!before.length) return { ok: false, clarify: `You're not working ${describeDaysShort(days)} at the moment — did you mean another day?` };
      slots = slots.filter((w) => !days.includes(w.weekday));
      changes.push(`Removed all of ${describeDaysShort(days)}`);
      continue;
    }
    if (!hasTimes) return { ok: false, clarify: `What times for “${c}”? For example “mornings” or “7–10”.` };
    const h = parseHours(stripped, days.length ? days : workingDays);
    if (!h.ok) return h;
    const target = h.value.slots;
    if (op === "add") {
      slots = normaliseSlots([...slots, ...target]);
      changes.push(`Added ${describeWeek(target)}`);
    } else if (op === "remove") {
      slots = subtract(slots, target);
      changes.push(`Removed ${describeWeek(target)}`);
    } else {
      const affected = [...new Set(target.map((w) => w.weekday))];
      slots = normaliseSlots([...slots.filter((w) => !affected.includes(w.weekday)), ...target]);
      changes.push(`${describeWeek(target)} (replacing ${describeDaysShort(affected)})`);
    }
    if (h.value.defaultsUsed.length) changes[changes.length - 1] += ` — taking ${h.value.defaultsUsed.join(" and ")}`;
  }
  if (!changes.length) return { ok: false, clarify: "I couldn't see a change there. Try “add Sunday mornings” or “drop Friday evenings”." };
  return ok({ slots, changes });
}

function subtract(slots: Window[], cut: Window[]): Window[] {
  const out: Window[] = [];
  for (const s of slots) {
    let pieces: [number, number][] = [[toMin(s.startTime), toMin(s.endTime)]];
    for (const c of cut.filter((x) => x.weekday === s.weekday)) {
      const a = toMin(c.startTime);
      const b = toMin(c.endTime);
      pieces = pieces.flatMap(([x, y]) => (b <= x || a >= y ? [[x, y] as [number, number]] : ([[x, a], [b, y]] as [number, number][]).filter(([p, q]) => q > p)));
    }
    for (const [x, y] of pieces) out.push({ weekday: s.weekday, startTime: toHHMM(x), endTime: toHHMM(y) });
  }
  return normaliseSlots(out);
}
const describeDaysShort = (d: Weekday[]) => describeDays(d);

// ------------------------------------------------------------------ client time limits
/** A client's extra detail on top of the parts of the day they picked: "not before 8:30am", "finish by 7 on Fridays". */
export interface TimeLimit {
  /** Days it applies to; empty = every day they picked. */
  days: Weekday[];
  notBefore?: string;
  finishBy?: string;
}

/** "8:30am" / "8.30" / "6pm" / "18:00" / "noon" → minutes. A bare hour ≤ 7 reads as pm for "finish by", am for "not before". */
function clockMin(raw: string, kind: "start" | "end" | "after"): number | null {
  const t = raw.trim().toLowerCase();
  if (/^(noon|midday|lunch ?time)$/.test(t)) return 12 * 60;
  const m = /^(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?$/.exec(t) ?? /^(\d{1,2})(\d{2})\s*(am|pm)?$/.exec(t);
  if (!m) return null;
  let h = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  const ap = m[3]?.replace(/\./g, "");
  if (h > 23 || min > 59) return null;
  if (ap === "pm" && h < 12) h += 12;
  else if (ap === "am" && h === 12) h = 0;
  else if (!ap && h >= 1 && h <= 7 && (kind === "end" || kind === "after")) h += 12; // "finish by 7", "after 6" = evening
  else if (!ap && h >= 1 && h <= 5 && kind === "start") h += 12; // "not before 5" for a person's training means 5pm
  return h * 60 + min;
}

const TIME = String.raw`(\d{1,2}(?:[:.]\d{2})?\s*(?:am|pm|a\.m\.|p\.m\.)?|\d{3,4}|noon|midday)`;
const NEG = String.raw`(?:not|don'?t|do not|can'?t|cannot|never|no)\s+(?:start\s+|train\s+|go\s+|be there\s+|finish\s+|stay\s+)?(?:any\s?)?`;
/** Most specific first; each match uses up its words so "not before 8" isn't also read as "before 8". */
const LIMIT_RULES: { kind: "start" | "end"; read?: "after"; re: RegExp }[] = [
  { kind: "end", re: new RegExp(String.raw`\b${NEG}(?:after|later than)\s+${TIME}`, "i") },
  { kind: "start", re: new RegExp(String.raw`\b${NEG}(?:before|earlier than)\s+${TIME}`, "i") },
  { kind: "end", re: new RegExp(String.raw`\b(?:finish(?:ed)?|done|out|leave|gone|home|end)\s+(?:by|before|at)\s+${TIME}`, "i") },
  { kind: "end", re: new RegExp(String.raw`\b(?:no later than|latest(?:\s+is)?)\s+${TIME}`, "i") },
  { kind: "start", read: "after", re: new RegExp(String.raw`\bafter\s+${TIME}`, "i") },
  { kind: "start", re: new RegExp(String.raw`\b(?:no earlier than|earliest(?:\s+is)?|from|starting(?:\s+at)?|start\s+at)\s+${TIME}`, "i") },
  { kind: "end", re: new RegExp(String.raw`\b(?:before|until|till|til|by)\s+${TIME}`, "i") },
];

/**
 * Read limits like "not before 8:30am", "finish by 7pm on Fridays, after 6 on Tuesdays".
 * Each clause (split on commas, "and", "but", ";") may name its own days; a clause without days
 * carries on from the one before. Returns null when no time is stated.
 */
export function parseTimeLimits(text: string): TimeLimit[] | null {
  const clauses = text
    .toLowerCase()
    .replace(/[–—]/g, "-")
    .replace(/\bbetween\s+(\S+(?:\s*[ap]m)?)\s+and\s+/g, "$1-") // "between 10am and 2pm" is one range, not two clauses
    .split(/[,;]|\band\b|\bbut\b|\balso\b|\.(?!\d)/)
    .map((c) => c.trim())
    .filter(Boolean);
  const out: TimeLimit[] = [];
  let lastDays: Weekday[] = [];
  for (const clause of clauses) {
    let c = ` ${clause} `;
    let a: number | null = null;
    let b: number | null = null;
    for (const r of LIMIT_RULES) {
      const m = r.re.exec(c);
      if (!m) continue;
      const v = clockMin(m[1]!, r.read ?? r.kind);
      if (v === null) continue;
      if (r.kind === "start" && a === null) a = v;
      if (r.kind === "end" && b === null) b = v;
      c = c.replace(m[0], " ");
    }
    if (a === null && b === null) {
      const range = /(\d{1,2}(?:[:.]\d{2})?\s*(?:am|pm)?)\s*(?:-|to|till|until)\s*(\d{1,2}(?:[:.]\d{2})?\s*(?:am|pm)?)/.exec(c);
      if (range) {
        a = clockMin(range[1]!, "start");
        b = clockMin(range[2]!, "end");
        if (a !== null && b !== null && b <= a && b < 12 * 60) b += 12 * 60;
      }
    }
    if (a === null && b === null) continue;
    const named = extractDays(clause);
    const days = named.length ? named : lastDays;
    if (named.length) lastDays = named;
    out.push({ days, ...(a !== null ? { notBefore: toHHMM(a) } : {}), ...(b !== null ? { finishBy: toHHMM(b) } : {}) });
  }
  return out.length ? out : null;
}

/** Narrow a client's picked times by their limits. Windows shorter than `minMinutes` are dropped. */
export function applyTimeLimits<P extends { weekday: Weekday; startTime?: string; endTime?: string }>(prefs: P[], limits: TimeLimit[], minMinutes: number, dayStart = "06:00", dayEnd = "21:00"): P[] {
  const out: P[] = [];
  for (const p of prefs) {
    const mine = limits.filter((l) => !l.days.length || l.days.includes(p.weekday));
    if (!mine.length) {
      out.push(p); // untouched — "any time" stays any time
      continue;
    }
    let a = toMin(p.startTime ?? dayStart);
    let b = toMin(p.endTime ?? dayEnd);
    for (const l of mine) {
      if (l.notBefore) a = Math.max(a, toMin(l.notBefore));
      if (l.finishBy) b = Math.min(b, toMin(l.finishBy));
    }
    if (b - a >= minMinutes) out.push({ ...p, startTime: toHHMM(a), endTime: toHHMM(b) });
  }
  return out;
}

// ------------------------------------------------------------------ date of birth
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/**
 * A UK-style date of birth: "12/03/1990", "12-3-90", "12 March 1990", "March 12 1990", "1990-03-12".
 * Day comes first in numeric forms. Two-digit years are taken as the most recent past year.
 * Returns "YYYY-MM-DD", or null if it isn't a real date in the past.
 */
export function parseDateOfBirth(text: string, now = new Date()): string | null {
  const t = text.trim().toLowerCase().replace(/(\d)(st|nd|rd|th)\b/g, "$1").replace(/,/g, " ").replace(/\s+/g, " ");
  let d: number, m: number, y: number;
  let r: RegExpExecArray | null;
  if ((r = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(t))) [y, m, d] = [Number(r[1]), Number(r[2]), Number(r[3])];
  else if ((r = /^(\d{1,2})[-/. ](\d{1,2})[-/. ](\d{2}|\d{4})$/.exec(t))) [d, m, y] = [Number(r[1]), Number(r[2]), Number(r[3])];
  else if ((r = /^(\d{1,2}) ([a-z]+)\.? (\d{2}|\d{4})$/.exec(t)) || (r = /^([a-z]+)\.? (\d{1,2}) (\d{2}|\d{4})$/.exec(t))) {
    const monthWord = /^\d/.test(r[1]!) ? r[2]! : r[1]!;
    const day = /^\d/.test(r[1]!) ? r[1]! : r[2]!;
    const mi = MONTHS.indexOf(monthWord.slice(0, 3));
    if (mi < 0) return null;
    [d, m, y] = [Number(day), mi + 1, Number(r[3])];
  } else return null;
  if (y < 100) {
    const yy = now.getUTCFullYear() % 100;
    y += y <= yy ? 2000 : 1900;
  }
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null; // 31/02 etc.
  if (date > now || now.getUTCFullYear() - y > 120) return null;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Whole years old on `now` (UK date). */
export function ageOn(dob: string, now = new Date()): number {
  const [y, m, d] = dob.split("-").map(Number) as [number, number, number];
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(now).split("-").map(Number) as [number, number, number];
  return today[0] - y - (today[1] < m || (today[1] === m && today[2] < d) ? 1 : 0);
}
